import { useContext, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import { UMButton, UMLoader } from '@urbanmetrix/um-kit';
import { ViewContext } from 'components/lib';
import { DeleteIcon, UploadIcon } from 'components/icons';
import { getApiErrorMessage } from 'api/apiClient';
import { getProjectImportedFiles, uploadProjectImportedFile } from 'api/projectApi';

type ImportedFileKind = 'customObstruction' | 'projectSketches';

export type ProjectImportedFile = {
  id: string | number;
  name?: string | null;
  kind?: string;
  deletedAt?: string | Date | null;
};

type Props = {
  projectId: string | null;
  kind: ImportedFileKind;
  selectedFile?: ProjectImportedFile | null;
  selectLabel: string;
  onSelect: (file: ProjectImportedFile) => void;
  onClear: () => void;
  disabled?: boolean;
  uploadDisabled?: boolean;
};

const LABELS: Record<ImportedFileKind, string> = {
  customObstruction: 'Custom Obstruction',
  projectSketches: 'Project Sketch',
};

const ProjectImportedFileDialog = ({
  projectId,
  kind,
  selectedFile,
  selectLabel,
  onSelect,
  onClear,
  disabled = false,
  uploadDisabled = false,
}: Props) => {
  const context = useContext(ViewContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<ProjectImportedFile[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const loadFiles = async () => {
    if (!projectId) return;
    setIsLoading(true);
    try {
      const response = await getProjectImportedFiles(projectId);
      setFiles((Array.isArray(response) ? response : []).filter((file) => file.kind === kind && !file.deletedAt));
    } catch (error) {
      context.notification.show(getApiErrorMessage(error, 'Unable to load imported files'), 'error', true, 'toast');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    setSelectedId(selectedFile?.id == null ? '' : String(selectedFile.id));
    setOpen(true);
    void loadFiles();
  };

  const handleUseSelected = () => {
    const file = files.find((item) => String(item.id) === selectedId);
    if (!file) return;
    onSelect(file);
    setOpen(false);
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !projectId) return;
    if (!file.name.toLowerCase().endsWith('.obj')) {
      context.notification.show('Only .obj files are allowed', 'error', true, 'toast');
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await uploadProjectImportedFile(projectId, kind, file);
      onSelect(uploaded);
      setOpen(false);
      context.notification.show(`${file.name} was uploaded`, 'success', true, 'toast');
    } catch (error) {
      context.notification.show(getApiErrorMessage(error, 'Unable to upload file'), 'error', true, 'toast');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="geodata-upload-block geodata-imported-file-picker">
        {selectedFile ? (
          <div className="geodata-imported-file-picker__file">
            <Button
              className="geodata-imported-file-picker__delete"
              aria-label={`Remove ${LABELS[kind]}`}
              title={`Remove ${LABELS[kind]}`}
              onClick={onClear}
              disabled={disabled}
            >
              <DeleteIcon />
            </Button>
            <p className="grid-muifile-input__filename">{selectedFile.name || 'Unnamed OBJ'}</p>
          </div>
        ) : (
          <UMButton text={selectLabel} onClick={handleOpen} disabled={disabled || !projectId} />
        )}
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { backgroundColor: '#ececec', maxWidth: 480 } } }}
      >
        <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#ececec' }}>
          Choose {LABELS[kind]} (only .obj files)
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#ececec' }}>
          {isLoading ? (
            <UMLoader />
          ) : files.length ? (
            <List disablePadding>
              {files.map((file) => (
                <ListItemButton
                  key={file.id}
                  selected={String(file.id) === selectedId}
                  onClick={() => setSelectedId(String(file.id))}
                  sx={{
                    px: 0,
                    py: 0.25,
                    minHeight: 36,
                    '&:hover': { backgroundColor: 'transparent' },
                    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  <Radio checked={String(file.id) === selectedId} sx={{ p: 0, mr: 1.5 }} />
                  <ListItemText
                    sx={{ my: 0, minWidth: 0 }}
                    primary={file.name || 'Unnamed OBJ'}
                    slotProps={{ primary: { noWrap: true } }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography sx={{ py: 2 }}>No {LABELS[kind].toLowerCase()} files found.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px 20px', backgroundColor: '#ececec' }}>
          <Button variant="contained" className="modal-no-btn" onClick={() => setOpen(false)}>
            CANCEL
          </Button>
          <Tooltip title="Upload new OBJ" arrow>
            <span className="geodata-imported-file-dialog__upload-tooltip">
              <Button
                variant="contained"
                className="modal-no-btn geodata-imported-file-dialog__upload"
                aria-label="Upload new OBJ"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || uploadDisabled}
              >
                {isUploading ? <UMLoader /> : <UploadIcon />}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            className="modal-yes-btn"
            onClick={handleUseSelected}
            disabled={!selectedId || isUploading}
          >
            USE
          </Button>
        </DialogActions>
      </Dialog>
      <input ref={fileInputRef} hidden type="file" accept=".obj" onChange={handleUpload} />
    </>
  );
};

export default ProjectImportedFileDialog;
